// src/pages/TranslonExplorer/index.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Container, Row, Col, Button } from "reactstrap";
import { toast, ToastContainer } from "react-toastify";
import { FadeLoader } from "react-spinners";

// Common Components
import BreadCrumb from "../../Components/Common/BreadCrumb";
import Files from "../../Components/Common/Files";
import SelectedFilesDisplay from "../../Components/Common/SelectedFilesDisplay";
import PlotContainer from "../../Components/Common/PlotContainer";
import SeqTypesAndGroups from "../../Components/Common/SeqTypesAndGroups";

// Page-specific Components
import ORFTypeSelection from "./components/ORFTypeSelection";
import FeaturesAndFilters from "./components/FeaturesAndFilters";

// API
import {
  fetchTranslonExplorerData,
  submitTranslonExplorerJob,
} from "../../helpers/eirna_backend_helper";

// Hooks
import { useFileSelection } from "../../hooks/useFileSelection";
import { useJobPolling } from "../../hooks/useJobPolling";
import { useSmartReadLength } from "../../hooks/useSmartReadLength";
import { useTranslonExplorerSettings } from "./hooks/useTranslonExplorerSettings";
import { EnterButtonListener } from "../../hooks/EnterButtonListener";
import { useDefaultFileSelection } from "../../hooks/useDefaultFileSelection";
import { useScrollToRef } from "../../hooks/useScrollToRef";

// Short code handling
import {
  useTranslonExplorerShortCode,
  createPlotSpecificPayload,
} from "../../hooks/useShortCodeHandlers";
import ActionButton from "../../Components/Common/ActionButton";

const TranslonExplorer = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // State
  const [loading, setLoading] = useState(true);
  const [tableData, setTableData] = useState(null);
  const [error, setError] = useState(null);
  const [chooserOpen, setChooserOpen] = useState(false);

  // References to plotContainer and loadingDiv, to allow for auto scrolling
  const loadingDivRef  = useRef(null);
  const plotContainerRef  = useRef(null);

  const [apiData, setApiData] = useState({
    json_files: [],
    organism: "",
    transcriptome: "",
  });

  // Hooks
  const {
    settings,
    updateSetting,
    resetSettings,
    validateSettings,
    updateMultipleSettings,
  } = useTranslonExplorerSettings();

  const {
    selectedFiles,
    selectedSeqType,
    selectedGroup,
    toggleFile,
    toggleAllFiles,
    setSelectedSeqType,
    setSelectedGroup,
    clearSelection,
    selectMultipleFiles,
    deselectMultipleFiles,
    setSelectedFiles,
  } = useFileSelection("riboseq");

  const { plotLoading, taskId, startJob } = useJobPolling();

  const {
    getStandardReadLengths,
    markFieldAsUserModified,
    resetUserModifications,
    shouldAutoAdjust,
  } = useSmartReadLength();

  // Use the Translon Explorer specific short code handler
  const { autoRunPlot, 
          setAutoRunPlot,
          consumeUserPassedShortCode } =
    useTranslonExplorerShortCode({
      location,
      updateMultipleSettings,
      setSelectedFiles,
    });

  // Clear results when ORF type changes to avoid confusion
  useEffect(() => {
    // Clear existing results and errors
    setTableData(null);
    setError(null);
  }, [settings.orfType, clearSelection]);

  // A callback used by the EnterButtonListener to generate the plot
  const runPlot = useCallback(() => {
    if (plotLoading) return;
    setAutoRunPlot(true);
  }, [plotLoading]);

  // Listens for the Enter button being pressed, if so calls runPlot (as long as
  // a textarea is not in focus)
  EnterButtonListener({
    onEnter: runPlot,
    modalOpen: chooserOpen,
    enabled: true,
  });

  // Check all riboseq checkboxes by default
  useDefaultFileSelection({apiData,
                           selectMultipleFiles,
                           fileType: "riboseq"})

  // Scroll to loadingDiv anytime plotLoading changes
  useScrollToRef(loadingDivRef, plotLoading);

  // Scroll to plot/table anytime tableData changes
  useScrollToRef(plotContainerRef, tableData);

  // Load data on component mount
  useEffect(() => {
    if (!projectId) return;

    const loadData = async () => {
      try {
        setLoading(true);
        const response = await fetchTranslonExplorerData(projectId);
        const data = response?.data || response || {};

        setApiData({
          json_files: data.json_files || [],
          organism: data.organism || "",
          transcriptome: data.transcriptome || "",
        });

        // Set default seq type
        const fileTypes = data.json_files
          ? [...new Set(data.json_files.map((x) => x.file_type))]
          : [];
        const defaultSeqType = fileTypes.includes("riboseq")
          ? "riboseq"
          : fileTypes[0];
        if (defaultSeqType) {
          setSelectedSeqType(defaultSeqType);
        }

        // Set default group
        const uniqueGroups = [
          ...new Set(
            data.json_files?.map((file) => file.subgroup).filter(Boolean) || []
          ),
        ];
        if (uniqueGroups.length > 0) {
          setSelectedGroup(uniqueGroups[0]);
        }

        toast.success("Project data loaded successfully");
      } catch (err) {
        console.error("Failed to load project data:", err);
        const errorMsg = "Failed to load project data";
        setError(errorMsg);
        toast.error(errorMsg);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [projectId, setSelectedSeqType, setSelectedGroup]);

  // Auto-trigger analysis generation when autoRunPlot is true
  useEffect(() => {
    if (autoRunPlot) {
      console.log("Auto-run triggered, checking conditions...", {
        autoRunPlot,
        plotLoading,
        loading,
        selectedFilesCount: selectedFiles.length,
      });

      if (!plotLoading && !loading && selectedFiles.length > 0) {
        console.log(
          "All conditions met, generating Translon Explorer analysis from short code..."
        );
        setAutoRunPlot(false);
        setTimeout(() => {
          handleGenerate();
        }, 50);
      } else {
        console.log(
          "Conditions not met yet, will retry when conditions change"
        );
      }
    }
  }, [autoRunPlot, plotLoading, loading, selectedFiles.length, setAutoRunPlot]);

  // Smart read length adjustment
  useEffect(() => {
    if (selectedFiles.length > 0 && apiData.json_files?.length > 0) {
      const currentMinRead = parseInt(settings.filters.minReadLen) || 20;
      const currentMaxRead = parseInt(settings.filters.maxReadLen) || 35;

      if (
        shouldAutoAdjust("minReadLen", currentMinRead, currentMaxRead) &&
        shouldAutoAdjust("maxReadLen", currentMinRead, currentMaxRead)
      ) {
        const { min, max } = getStandardReadLengths(
          selectedFiles,
          apiData.json_files
        );

        if (
          settings.filters.minReadLen !== String(min) ||
          settings.filters.maxReadLen !== String(max)
        ) {
          updateSetting("filters", {
            ...settings.filters,
            minReadLen: String(min),
            maxReadLen: String(max),
          });
          toast.info(`Read lengths auto-adjusted: Min=${min}, Max=${max}`, {
            autoClose: 3000,
          });
        }
      }
    }
  }, [
    selectedFiles,
    apiData.json_files,
    getStandardReadLengths,
    shouldAutoAdjust,
    updateSetting,
    settings.filters.minReadLen,
    settings.filters.maxReadLen,
  ]);

  // Computed values
  const filteredFiles = useMemo(() => {
    if (!Array.isArray(apiData.json_files)) return [];
    let files = apiData.json_files;
    if (selectedSeqType && selectedSeqType !== "all") {
      files = files.filter((x) => x.file_type === selectedSeqType);
    }
    if (selectedGroup && selectedGroup !== "all") {
      files = files.filter((x) => x.subgroup === selectedGroup);
    }
    return files;
  }, [apiData.json_files, selectedSeqType, selectedGroup]);

  const seqTypes = useMemo(() => {
    if (!Array.isArray(apiData.json_files)) return [];
    const types = [...new Set(apiData.json_files.map((x) => x.file_type))];
    return types.map((type) => ({
      value: type,
      label: type.charAt(0).toUpperCase() + type.slice(1),
      count: apiData.json_files.filter((x) => x.file_type === type).length,
    }));
  }, [apiData.json_files]);

  const availableGroups = useMemo(() => {
    if (!Array.isArray(apiData.json_files)) return [];
    const groupSet = new Set(
      apiData.json_files.map((x) => x.subgroup).filter(Boolean)
    );
    return Array.from(groupSet).map((group) => ({
      value: group,
      label: group,
      count: apiData.json_files.filter((x) => x.subgroup === group).length,
    }));
  }, [apiData.json_files]);

  const allSeqTypeFilesSelected = useMemo(() => {
    if (!selectedSeqType || selectedSeqType === "all") return false;
    // Use all files, not filtered files, and filter only by seq type
    const seqTypeFiles = (apiData.json_files || []).filter(
      (x) => x.file_type === selectedSeqType
    );
    return (
      seqTypeFiles.length > 0 &&
      seqTypeFiles.every((x) => selectedFiles.includes(x.file_id))
    );
  }, [selectedSeqType, apiData.json_files, selectedFiles]);

  const allGroupFilesSelected = useMemo(() => {
    if (!selectedGroup || selectedGroup === "all") return false;
    const groupFiles = filteredFiles.filter(
      (x) => x.subgroup === selectedGroup
    );
    return (
      groupFiles.length > 0 &&
      groupFiles.every((x) => selectedFiles.includes(x.file_id))
    );
  }, [selectedGroup, filteredFiles, selectedFiles]);

  // Event handlers
  const handleSettingUpdate = useCallback(
    (key, value) => {
      updateSetting(key, value);
      // Mark read length fields as user modified when manually changed
      if (
        key === "filters" &&
        (value.minReadLen !== undefined || value.maxReadLen !== undefined)
      ) {
        if (value.minReadLen !== undefined) {
          markFieldAsUserModified("minReadLen");
        }
        if (value.maxReadLen !== undefined) {
          markFieldAsUserModified("maxReadLen");
        }
      }
    },
    [updateSetting, markFieldAsUserModified]
  );

  const handleReset = useCallback(() => {
    resetSettings();
    resetUserModifications();
    clearSelection();
    setTableData(null);
    setError(null);
    toast.info("Settings reset to defaults");
  }, [resetSettings, resetUserModifications, clearSelection]);

  const handleSelectAllSeqTypeFiles = useCallback(
    (checked) => {
      if (!selectedSeqType || selectedSeqType === "all") return;
      // Use all files, not filtered files, and filter only by seq type
      const seqTypeFiles = (apiData.json_files || []).filter(
        (x) => x.file_type === selectedSeqType
      );
      const fileIds = seqTypeFiles.map((x) => x.file_id);

      if (checked) {
        selectMultipleFiles(fileIds);
      } else {
        deselectMultipleFiles(fileIds);
      }
    },
    [
      selectedSeqType,
      apiData.json_files,
      selectMultipleFiles,
      deselectMultipleFiles,
    ]
  );

  const handleSelectAllGroupFiles = useCallback(
    (checked) => {
      if (!selectedGroup || selectedGroup === "all") return;
      const groupFiles = filteredFiles.filter(
        (x) => x.subgroup === selectedGroup
      );
      const fileIds = groupFiles.map((x) => x.file_id);

      if (checked) {
        selectMultipleFiles(fileIds);
      } else {
        deselectMultipleFiles(fileIds);
      }
    },
    [selectedGroup, filteredFiles, selectMultipleFiles, deselectMultipleFiles]
  );

  // Build payload for API
  const buildPayload = useCallback(() => {
    const shortCodeForThisRequest = consumeUserPassedShortCode();
    // Use the Translon Explorer specific payload creator
    return createPlotSpecificPayload.translonExplorer({
      settings,
      selectedFiles,
      projectId,
      userPassedShortCode: shortCodeForThisRequest,
    });
  }, [settings, selectedFiles, projectId, consumeUserPassedShortCode]);

  // Generate analysis
  const handleGenerate = useCallback(async () => {
    if (plotLoading) {
      console.log("Job already in progress");
      return;
    }

    const validation = validateSettings();
    if (!validation.isValid) {
      toast.error(validation.errors[0]);
      return;
    }

    if (selectedFiles.length === 0) {
      toast.error("Please select at least one file");
      return;
    }

    const payload = buildPayload();
    console.log("Submitting translon explorer payload:", payload);

    const onSuccess = (result) => {
      setTableData(result);
      setError(null);
    };

    const onError = (errorMsg) => {
      setError(errorMsg);
      setTableData(null);
    };

    startJob(submitTranslonExplorerJob, payload, onSuccess, onError);
  }, [
    plotLoading,
    validateSettings,
    selectedFiles.length,
    buildPayload,
    startJob,
  ]);

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="loading-overlay">
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "75vh",
              }}
            >
              <FadeLoader color="#2F9E37" size={50} />
            </div>
          </div>
        </Container>
      </div>
    );
  }

  document.title = "Translon Explorer | EIRNA Bio Connect";

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            breadcrumbs={[
              { label: "Home", url: "/dashboard" },
              {
                label: "Select Function",
                url: `/projects/${projectId}/select-function`,
              },
              { label: "Translon Explorer" },
            ]}
          />

          <Row className="mb-4">
            <Col lg={12}>
              <div className="d-flex justify-content-between align-items-center">
                <h4
                  style={{
                    fontSize: "24px",
                    fontWeight: "600",
                    color: "#1f2937",
                    marginBottom: "0",
                  }}
                >
                  Translon Explorer
                </h4>
                <div className="d-flex align-items-center gap-3">
                  <Button
                    color="outline-secondary"
                    size="sm"
                    onClick={handleReset}
                    disabled={plotLoading}
                  >
                    <i className="ri-refresh-line me-1"></i>
                    Reset All
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          {/* ORF Type Selection */}
          <Row className="mb-1">
            <Col lg={12}>
              <ORFTypeSelection
                selectedType={settings.orfType}
                onTypeChange={(type) => handleSettingUpdate("orfType", type)}
              />
            </Col>
          </Row>

          {/* Features and Filters */}
          <Row className="mb-4">
            <Col lg={12}>
              <FeaturesAndFilters
                settings={settings}
                onUpdateSetting={handleSettingUpdate}
              />
            </Col>{" "}
          </Row>

          <Row className="mb-4">
            <Col lg={12}>
              <SeqTypesAndGroups
                seqTypes={seqTypes}
                groups={availableGroups}
                selectedSeqType={selectedSeqType}
                selectedGroup={selectedGroup}
                onSelectSeqType={setSelectedSeqType}
                onSelectGroup={setSelectedGroup}
                onSelectAllSeqTypeFiles={handleSelectAllSeqTypeFiles}
                onSelectAllGroupFiles={handleSelectAllGroupFiles}
                selectedFiles={selectedFiles}
                filteredFiles={filteredFiles}
                allSeqTypeFilesSelected={allSeqTypeFilesSelected}
                allGroupFilesSelected={allGroupFilesSelected}
                showSelectAllCheckboxes={true}
                customTitle="Seq Types & Groups"
                customIcon="ri-tools-fill"
              />
            </Col>
          </Row>

          {/* Files Card */}
          <Row className="mb-2">
            <Col lg={12}>
              <Files
                files={filteredFiles}
                selectedFiles={selectedFiles}
                onToggleFile={toggleFile}
                onSelectAllGroupFiles={handleSelectAllGroupFiles}
                title="Select Files"
                searchable={true}
                selectable={true}
                filesPerPage={30}
                columns={6}
                showFileCount={true}
                customSearchFields={["file_name", "file_description"]}
              />

              
            </Col>
          </Row>
          
          {/* Selected Files Display */}
          <Row>
            <Col lg={12}>
              <SelectedFilesDisplay
                selectedFiles={selectedFiles}
                allFiles={apiData.json_files}
                title="Selected Files for Translon Explorer"
                showSearch={true}
                maxDisplayFiles={0}
              />
            </Col>
          </Row>

          {/* Status and Generate Button */}
          <Row className="mb-4">
            <Col lg={12}>
              <div ref={loadingDivRef} 
                   className="d-flex justify-content-between align-items-center">
                <div className="text-muted">
                  {selectedFiles.length === 0 ? (
                    <span>
                      <i className="ri-file-list-line me-1"></i>
                      Please select at least one file to start analysis
                    </span>
                  ) : (
                    <span>
                      <i className="ri-check-circle-line me-1 text-success"></i>
                      Ready to analyze with{" "}
                      <strong>{selectedFiles.length}</strong> file
                      {selectedFiles.length !== 1 ? "s" : ""} using{" "}
                      <strong>{settings.orfType.toUpperCase()}</strong> ORF
                      type
                    </span>
                  )}
                  {taskId && (
                    <span className="ms-2">
                      - <strong>Task ID:</strong> {taskId}
                    </span>
                  )}
                </div>
                <ActionButton
                  onClick={handleGenerate}
                  loading={plotLoading}
                  buttonType="table"
                />{" "}
              </div>
            </Col>
          </Row>

          {/* Results Container */}
          {(tableData || plotLoading || error) && (
            <Row>
              <Col lg={12}>
                <PlotContainer
                  ref={plotContainerRef}
                  plotData={tableData}
                  loading={plotLoading}
                  error={error}
                  selectedFilesCount={selectedFiles.length}
                  title="Translon Explorer Results"
                  onRetry={handleGenerate}
                  showFileCount={true}
                  isTable={true}
                />
              </Col>
            </Row>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default TranslonExplorer;
